import React, { useState, useRef, useEffect } from 'react';

/**
 * Curated upper-bound stops for the max-height filter (in cm).
 * The plant height data is heavily right-skewed (most garden plants < 300 cm,
 * a few trees up to 5000 cm), so a linear slider would cram the common range
 * into a sliver. These uneven stops give useful resolution where it matters.
 * The trailing `null` is the "Alle Höhen" (no filter) position.
 */
export const MAX_HEIGHT_STOPS = [30, 60, 100, 150, 200, 300, 500, 1000, 2000, null];

/**
 * Format a stop value for display: cm below 1 m, meters (German comma) above.
 */
const formatStop = (cm) => {
  if (cm == null) return 'Alle Höhen';
  if (cm < 100) return `bis ${cm} cm`;
  return `bis ${(cm / 100).toLocaleString('de-DE')} m`;
};

/**
 * Index of a filter value within MAX_HEIGHT_STOPS. null → the "off" stop.
 */
const indexForValue = (value) => {
  if (value == null) return MAX_HEIGHT_STOPS.length - 1;
  const idx = MAX_HEIGHT_STOPS.indexOf(value);
  return idx === -1 ? MAX_HEIGHT_STOPS.length - 1 : idx;
};

/**
 * MaxHeightSlider - Stepped upper-bound slider for maximum growth height (#762).
 *
 * Isolated (and memoized) like PhSlider so the parent PlantCatalogSidebar does
 * not re-render on every slider tick. The slider position is an index into
 * MAX_HEIGHT_STOPS; onChange emits the cm value (or null when the "Alle Höhen"
 * stop is selected). Updates are debounced (300ms) for smooth dragging.
 *
 * @param {Object} props
 * @param {number|null} props.value - Current max-height filter value in cm
 * @param {Function} props.onChange - Callback when filter should update (cm or null)
 */
const MaxHeightSlider = ({ value, onChange }) => {
  const lastIndex = MAX_HEIGHT_STOPS.length - 1;

  // Local index for smooth slider movement
  const [tempIndex, setTempIndex] = useState(() => indexForValue(value));
  const [prevValue, setPrevValue] = useState(value);
  const timeoutRef = useRef(null);

  // Sync with parent value when it changes externally (e.g., reset) — adjusted
  // during render rather than in an effect to avoid a setState-in-effect cascade.
  if (value !== prevValue) {
    setPrevValue(value);
    setTempIndex(indexForValue(value));
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const newIndex = parseInt(e.target.value, 10);

    // Update local state immediately for smooth UI
    setTempIndex(newIndex);

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the parent update (300ms)
    timeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(MAX_HEIGHT_STOPS[newIndex]);
      }
    }, 300);
  };

  const fillPercent = (tempIndex / lastIndex) * 100;
  const isActive = value != null;

  return (
    <div className="w-full px-2 py-2">
      {/* Label */}
      <label htmlFor="max-height-slider" className="block text-xs font-medium text-gray-600 mb-2">
        Maximale Wuchshöhe
      </label>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <input
            type="range"
            id="max-height-slider"
            min="0"
            max={lastIndex}
            step="1"
            value={tempIndex}
            onChange={handleChange}
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${fillPercent}%, #e5e7eb ${fillPercent}%, #e5e7eb 100%)`
            }}
            className="w-full h-2 rounded-full appearance-none cursor-pointer slider-emerald"
            aria-label="Maximale Wuchshöhe auswählen"
          />
          {/* Scale endpoints */}
          <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-0.5">
            <span>30 cm</span>
            <span>Alle</span>
          </div>
        </div>

        {/* Current stop display */}
        <div className="w-16 text-right">
          <span className="text-sm font-semibold text-emerald-600 block">
            {formatStop(MAX_HEIGHT_STOPS[tempIndex])}
          </span>
        </div>
      </div>

      {/* Reset button - only when filter is active */}
      {isActive && (
        <button
          onClick={() => {
            setTempIndex(lastIndex);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            if (onChange) {
              onChange(null);
            }
          }}
          className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 underline"
        >
          Höhen-Filter zurücksetzen
        </button>
      )}
    </div>
  );
};

export default React.memo(MaxHeightSlider);
